import {
  execute,
  MAIN_SITE_ROUTES,
  QUERY_KEYS,
  SPECIAL_PROJECTS_QUERY,
  type SpecialProjectType,
} from "@frontend/roz-sdk-core";
import { UiLoader } from "@frontend/roz-uikit";
import { Button } from "@frontend/roz-uikit/studio";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeftIcon } from "lucide-react";
import type { FC } from "react";

import { cn } from "@/lib";
import { NavLink } from "@/primitives";
import { useSheetStore } from "@/ui-state";

type ProjectsSectionProperties = {
  mobile?: boolean;
  onBack?: () => void;
};

const ProjectCard: FC<{
  project: SpecialProjectType;
  mobile?: boolean;
}> = ({ project, mobile = false }) => (
  <a
    className={cn(
      "sdkui:flex sdkui:items-center sdkui:gap-4 sdkui:rounded-3xl sdkui:p-2 sdkui:transition-colors sdkui:hover:bg-white",
      mobile ? "sdkui:w-full" : "sdkui:min-w-0"
    )}
    href={project.projectUrl || MAIN_SITE_ROUTES.projects}
    rel="noreferrer"
    target="_blank"
  >
    {project.photo && (
      <div
        aria-label={project.name || "Проект Российского Общества Знание"}
        className="sdkui:size-20 sdkui:shrink-0 sdkui:rounded-2xl sdkui:bg-center sdkui:bg-cover sdkui:bg-no-repeat"
        role="img"
        style={{ backgroundImage: `url(${project.photo})` }}
      />
    )}
    <span className="sdkui:text-base sdkui:text-dark-90 sdkui:leading-6">
      {project.name}
    </span>
  </a>
);

export const ProjectsSection = ({
  mobile = false,
  onBack,
}: ProjectsSectionProperties) => {
  const { close } = useSheetStore();
  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEYS.specialProjects,
    queryFn: () => execute(SPECIAL_PROJECTS_QUERY),
  });

  const projects = (data?.specialProjects || []) as SpecialProjectType[];

  if (mobile) {
    return (
      <div className="sdkui:flex sdkui:h-full sdkui:w-full sdkui:min-w-0 sdkui:flex-1 sdkui:flex-col sdkui:overflow-hidden sdkui:bg-dark-10">
        <div className="sdkui:flex sdkui:items-center sdkui:px-1 sdkui:py-5">
          <div className="sdkui:w-[50px]">
            <Button
              aria-label="Назад"
              className="sdkui:text-dark-80"
              iconOnly
              onClick={onBack}
              size="lg"
              variant="ghostPrimary"
            >
              <ChevronLeftIcon />
            </Button>
          </div>
          <div className="sdkui:flex-1 sdkui:text-center sdkui:text-dark-100 sdkui:text-xl sdkui:leading-7">
            Проекты
          </div>
          <div className="sdkui:flex sdkui:w-[50px] sdkui:justify-end">
            <Button
              asChild
              className="sdkui:px-3 sdkui:font-medium sdkui:text-dark-100 sdkui:text-xs sdkui:leading-4"
              size="sm"
              variant="ghostPrimary"
            >
              <NavLink href={MAIN_SITE_ROUTES.projects} onClick={close}>
                Все
              </NavLink>
            </Button>
          </div>
        </div>
        <div className="sdkui:flex-1 sdkui:overflow-y-auto sdkui:px-2 sdkui:pb-2">
          <div className="sdkui:flex sdkui:flex-col sdkui:gap-0.5">
            <UiLoader className="sdkui:min-h-40" loading={isLoading}>
              {projects.map((project) => (
                <ProjectCard key={project.id} mobile project={project} />
              ))}
            </UiLoader>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sdkui:flex sdkui:h-full sdkui:min-w-0 sdkui:flex-1 sdkui:flex-col sdkui:rounded-3xl sdkui:bg-dark-10 sdkui:p-4">
      <div className="sdkui:flex sdkui:items-center sdkui:justify-between sdkui:gap-4">
        <div className="sdkui:text-2xl sdkui:text-dark-100 sdkui:leading-8">
          Проекты
        </div>
        <Button
          asChild
          className="sdkui:px-6"
          size="lg"
          variant="outlinePrimary"
        >
          <NavLink href={MAIN_SITE_ROUTES.projects} onClick={close}>
            Все проекты
          </NavLink>
        </Button>
      </div>
      <div className="sdkui:mt-2 sdkui:grid sdkui:flex-1 sdkui:grid-cols-1 sdkui:gap-2 sdkui:overflow-y-auto sdkui:lg:grid-cols-2">
        <UiLoader
          className="sdkui:col-span-full sdkui:min-h-40"
          loading={isLoading}
        >
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </UiLoader>
      </div>
    </div>
  );
};
